// Owner-side staff hooks (slice 4b iteration).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OwnerCreateStaffRequest } from '@naya/shared'

import { ownerApi } from '../ownerApi.js'
import type {
  OwnerStaffRow,
  OwnerStaffListResponse,
  OwnerCreateStaffResponse,
  OwnerResetStaffPasswordResponse,
} from '../types.js'

export const staffKey = ['owner', 'staff'] as const

export function useStaff() {
  return useQuery<OwnerStaffListResponse>({
    queryKey: staffKey,
    queryFn: () => ownerApi.get<OwnerStaffListResponse>('/owner/staff'),
  })
}

export function useCreateStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OwnerCreateStaffRequest) =>
      ownerApi.post<OwnerCreateStaffResponse>('/owner/staff', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKey })
    },
  })
}

export function useDeleteStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (staffId: string) =>
      ownerApi.del<{ ok: true }>(`/owner/staff/${staffId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKey })
    },
  })
}

export function useResetStaffPassword() {
  const qc = useQueryClient()
  return useMutation({
    // The owner types the new password; it's sent in the body. (An empty
    // body would make the server generate one — the UI always supplies it.)
    mutationFn: (input: { staffId: string; password: string }) =>
      ownerApi.post<OwnerResetStaffPasswordResponse>(
        `/owner/staff/${input.staffId}/reset-password`,
        { password: input.password },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKey })
    },
  })
}

// Re-export so the page only needs to import the hook module.
export type { OwnerStaffRow }
